// src/admin/AdminRoleManagement.js
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client (same as in your SupabaseAuthProvider)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * This file contains functions for managing admin roles in your application.
 * You can use these functions directly in your SuperAdmin dashboard or
 * run them in a development environment to set up your initial admin user.
 */

/**
 * Assign admin role to a user
 * @param {string} userId - The user ID to assign admin role to
 * @returns {Promise<Object>} - Result of the operation
 */
export const assignAdminRole = async (userId) => {
  try {
    // First check if the user exists
    const { error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('id', userId)
      .single();

    if (userError) {
      return { success: false, error: 'User not found' };
    }

    // Update the user's role in your user_roles table
    const { error } = await supabase.from('user_roles').upsert([
      {
        user_id: userId,
        role: 'admin',
        updated_at: new Date(),
      },
    ]);

    if (error) {
      console.error('Error assigning admin role:', error);
      return { success: false, error: error.message };
    }

    // Update the user's metadata in the auth.users table
    const { error: updateError } = await supabase.auth.admin.updateUserById(
      userId,
      { user_metadata: { role: 'admin' } }
    );

    if (updateError) {
      console.error('Error updating user metadata:', updateError);
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
};

/**
 * Check if a user has admin role
 * @param {string} userId - The user ID to check
 * @returns {Promise<Object>} - Result with isAdmin flag
 */
export const checkAdminRole = async (userId) => {
  try {
    // Check user_roles table
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .single();

    if (error && error.code !== 'PGRST116') {
      // PGRST116 is the error code for "no rows returned"
      console.error('Error checking admin role:', error);
      return { success: false, error: error.message };
    }

    return {
      success: true,
      isAdmin: !!data,
    };
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
};

/**
 * Remove admin role from a user
 * @param {string} userId - The user ID to remove admin role from
 * @returns {Promise<Object>} - Result of the operation
 */
export const removeAdminRole = async (userId) => {
  try {
    // Remove from user_roles table
    const { error } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role', 'admin');

    if (error) {
      console.error('Error removing admin role:', error);
      return { success: false, error: error.message };
    }

    // Update the user's metadata in the auth.users table
    const { error: updateError } = await supabase.auth.admin.updateUserById(
      userId,
      { user_metadata: { role: null } }
    );

    if (updateError) {
      console.error('Error updating user metadata:', updateError);
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
};

/**
 * Get all users with admin role
 * @returns {Promise<Object>} - Result with list of admin users
 */
export const getAdminUsers = async () => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select(
        `
        user_id,
        users:user_id (
          email,
          created_at,
          last_sign_in_at
        )
      `
      )
      .eq('role', 'admin');

    if (error) {
      console.error('Error getting admin users:', error);
      return { success: false, error: error.message };
    }

    return {
      success: true,
      admins: data,
    };
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
};
